import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, MicOff, Square, Loader2, X, Check, Edit2, Trash2, AlertCircle, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_LOG_TYPES = [
  { key: "call_summary",          label: "Call Summary",          desc: "Quick recap of a phone call" },
  { key: "whatsapp_followup",     label: "WhatsApp Follow-up",    desc: "Note to capture a chat thread" },
  { key: "meeting_note",          label: "Meeting Note",          desc: "Outcome of an in-person meeting" },
  { key: "site_visit_debrief",    label: "Site Visit Debrief",    desc: "Full debrief after a visit" },
  { key: "task_completion_note",  label: "Task Completion Note",  desc: "Wrap-up note when closing a task" },
  { key: "internal_manager_note", label: "Internal Manager Note", desc: "Private note for the team" },
];

const STAGES = [
  "new", "contacted", "qualified", "interested", "site_visit",
  "negotiation", "booked", "lost", "dormant", "re_engaged",
];

export default function VoiceLogDialog({
  open,
  onOpenChange,
  leadId,
  defaultType = null,
  taskId = null,
  siteVisitId = null,
  onSaved = () => {},
}) {
  const [step, setStep] = useState("pick_type"); // pick_type | record | processing | review | error
  const [voiceLogType, setVoiceLogType] = useState(defaultType);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [structured, setStructured] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const [acceptTask, setAcceptTask] = useState(true);
  const [acceptStage, setAcceptStage] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(defaultType ? "record" : "pick_type");
      setVoiceLogType(defaultType);
      setSeconds(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setTranscript("");
      setStructured(null);
      setEditing(false);
      setEditedSummary("");
      setAcceptTask(true);
      setAcceptStage(false);
      setErrorMsg(null);
    } else {
      stopAndCleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType]);

  const stopAndCleanup = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current = null;
    setRecording(false);
  };

  const startRecording = async () => {
    setErrorMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        chunksRef.current = [];
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      setErrorMsg(err?.message || "Microphone permission denied or unavailable.");
      setStep("error");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
  };

  const submitForTranscription = async () => {
    if (!audioBlob || !leadId || !voiceLogType) return;
    setStep("processing");
    setErrorMsg(null);
    try {
      const fd = new FormData();
      fd.append("lead_id", leadId);
      fd.append("voice_log_type", voiceLogType);
      fd.append("audio", audioBlob, "voice.webm");
      if (taskId) fd.append("task_id", taskId);
      if (siteVisitId) fd.append("site_visit_id", siteVisitId);

      const res = await axios.post(`${API}/voice/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(res.data.transcript || "");
      setStructured(res.data.structured || null);
      setEditedSummary(res.data.structured?.summary || "");
      setAcceptStage(false); // never default to true
      setAcceptTask(!!res.data.structured?.suggested_task?.create);
      setStep("review");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Transcription failed. Please try again.");
      setStep("error");
    }
  };

  const saveToCRM = async (mode) => {
    setSaving(true);
    try {
      const body = {
        lead_id: leadId,
        voice_log_type: voiceLogType,
        transcript,
        edited_summary: editedSummary,
        extracted_signals: structured,
        save_mode: mode,
        selected_actions:
          mode === "transcript_only"
            ? {}
            : {
                create_task: acceptTask && !!structured?.suggested_task,
                task: structured?.suggested_task,
                apply_stage_change: acceptStage && !!structured?.suggested_stage?.stage,
                stage: structured?.suggested_stage?.stage,
              },
        task_id: taskId,
        site_visit_id: siteVisitId,
      };
      const res = await axios.post(`${API}/voice/save`, body);
      toast.success(
        mode === "transcript_only"
          ? "Transcript saved to lead history"
          : `Saved to CRM${res.data.task_created ? " · Task created" : ""}${res.data.stage_changed ? " · Stage updated" : ""}`
      );
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderTimer = () => {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="voice-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-violet-600" />
            {step === "review" ? "Review CRM Update" : "Voice Log"}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Don't type the CRM update. Say it. We'll structure it.</p>
        </DialogHeader>

        {/* Step 1: type picker */}
        {step === "pick_type" && (
          <div className="space-y-2 mt-2" data-testid="voice-log-type-picker">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Pick voice log type</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {VOICE_LOG_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setVoiceLogType(t.key); setStep("record"); }}
                  className="text-left p-3 rounded-lg border border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-colors"
                  data-testid={`voice-type-${t.key}`}
                >
                  <p className="text-sm font-semibold text-slate-700">{t.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: record */}
        {step === "record" && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4" data-testid="voice-log-record-step">
            <Badge variant="outline" className="text-xs">{VOICE_LOG_TYPES.find((t) => t.key === voiceLogType)?.label}</Badge>
            <div className={`relative w-32 h-32 rounded-full flex items-center justify-center transition-colors ${recording ? "bg-rose-100" : "bg-violet-100"}`}>
              {recording && (
                <span className="absolute inset-0 rounded-full bg-rose-300 animate-ping opacity-40" />
              )}
              <Mic className={`relative w-12 h-12 ${recording ? "text-rose-600" : "text-violet-600"}`} />
            </div>
            <p className="text-2xl font-mono font-semibold text-slate-700" data-testid="voice-timer">
              {renderTimer()}
            </p>
            <p className="text-xs text-slate-500 text-center max-w-md">
              {recording
                ? "Speak naturally. Tap stop when done."
                : audioBlob
                ? "Recording captured. Tap process or re-record."
                : "Tap the mic to start. Recommended: 10s – 3 min."}
            </p>
            <div className="flex items-center gap-2">
              {!recording && !audioBlob && (
                <Button onClick={startRecording} className="bg-violet-600 hover:bg-violet-700" data-testid="start-recording-btn">
                  <Mic className="w-4 h-4 mr-2" /> Start recording
                </Button>
              )}
              {recording && (
                <Button onClick={stopRecording} variant="destructive" data-testid="stop-recording-btn">
                  <Square className="w-4 h-4 mr-2" /> Stop
                </Button>
              )}
              {audioBlob && !recording && (
                <>
                  {audioUrl && <audio src={audioUrl} controls className="h-8" data-testid="audio-preview" />}
                  <Button variant="outline" size="sm" onClick={() => { setAudioBlob(null); setAudioUrl(null); setSeconds(0); }} data-testid="rerecord-btn">
                    <MicOff className="w-4 h-4 mr-1" /> Re-record
                  </Button>
                  <Button onClick={submitForTranscription} className="bg-violet-600 hover:bg-violet-700" data-testid="process-recording-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> Process
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3: processing */}
        {step === "processing" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4" data-testid="voice-log-processing-step">
            <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
            <p className="text-sm font-medium text-slate-700">Turning your voice note into a CRM update…</p>
            <p className="text-xs text-slate-500">Transcribing + extracting signals. Audio is deleted right after.</p>
          </div>
        )}

        {/* Step 4: review */}
        {step === "review" && (
          <div className="space-y-4 mt-2" data-testid="voice-log-review-step">
            {/* Confidence */}
            {structured?.confidence !== undefined && (
              <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                <p className="text-xs text-violet-700">
                  AI confidence: <span className="font-semibold">{Math.round((structured.confidence || 0) * 100)}%</span>
                </p>
                <Badge variant="outline" className="text-xs">
                  {VOICE_LOG_TYPES.find((t) => t.key === voiceLogType)?.label}
                </Badge>
              </div>
            )}

            {/* Transcript */}
            <details className="bg-slate-50 border border-slate-200 rounded-lg" data-testid="voice-transcript-section">
              <summary className="px-3 py-2 cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-600">
                Raw transcript ({transcript.split(" ").filter(Boolean).length} words)
              </summary>
              <div className="px-3 py-2 text-sm text-slate-700 whitespace-pre-wrap">{transcript || "—"}</div>
            </details>

            {/* AI Summary */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wide text-slate-500">AI summary (saved to lead history)</Label>
                <button onClick={() => setEditing((e) => !e)} className="text-xs text-violet-600 hover:underline flex items-center gap-1" data-testid="toggle-edit-summary">
                  <Edit2 className="w-3 h-3" /> {editing ? "Done" : "Edit"}
                </button>
              </div>
              {editing ? (
                <Textarea rows={3} value={editedSummary} onChange={(e) => setEditedSummary(e.target.value)} data-testid="edit-summary-textarea" />
              ) : (
                <p className="text-sm text-slate-800 bg-white border border-slate-200 rounded-lg p-3" data-testid="ai-summary-display">
                  {editedSummary || structured?.summary || "—"}
                </p>
              )}
            </div>

            {/* Extracted signals */}
            {structured && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="extracted-signals">
                <SignalsBlock title="Buying signals" items={structured.buying_signals} color="emerald" />
                <SignalsBlock title="Objections" items={structured.objections} color="rose" />
                <SignalsBlock title="Decision influencers" items={structured.decision_influencers} color="blue" />
                <SignalsBlock title="Budget concerns" items={structured.budget_concerns} color="amber" />
                {structured.competitor_mentions?.length > 0 && (
                  <SignalsBlock title="Competitors mentioned" items={structured.competitor_mentions} color="slate" />
                )}
                {structured.urgency && structured.urgency !== "unclear" && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-2.5">
                    <p className="text-[11px] uppercase tracking-wide text-violet-500 font-semibold mb-1">Urgency</p>
                    <p className="text-sm text-violet-900 capitalize">{structured.urgency}</p>
                  </div>
                )}
              </div>
            )}

            {/* Site visit debrief block */}
            {voiceLogType === "site_visit_debrief" && structured?.site_visit_debrief && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2" data-testid="site-debrief-block">
                <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Site visit debrief</p>
                {structured.site_visit_debrief.attendees?.length > 0 && (
                  <p className="text-xs text-teal-900"><strong>Attendees:</strong> {structured.site_visit_debrief.attendees.join(", ")}</p>
                )}
                {structured.site_visit_debrief.what_buyer_liked?.length > 0 && (
                  <p className="text-xs text-teal-900"><strong>Liked:</strong> {structured.site_visit_debrief.what_buyer_liked.join("; ")}</p>
                )}
                {structured.site_visit_debrief.what_buyer_disliked?.length > 0 && (
                  <p className="text-xs text-teal-900"><strong>Disliked:</strong> {structured.site_visit_debrief.what_buyer_disliked.join("; ")}</p>
                )}
                {structured.site_visit_debrief.family_influencer_dynamics && (
                  <p className="text-xs text-teal-900"><strong>Family/influencer dynamics:</strong> {structured.site_visit_debrief.family_influencer_dynamics}</p>
                )}
                <div className="flex gap-2 pt-1 text-xs">
                  <Badge variant="outline" className="capitalize">Revisit: {structured.site_visit_debrief.revisit_probability}</Badge>
                  <Badge variant="outline" className="capitalize">Negotiation: {structured.site_visit_debrief.negotiation_readiness}</Badge>
                </div>
                {structured.site_visit_debrief.follow_up_strategy && (
                  <p className="text-xs text-teal-900"><strong>Strategy:</strong> {structured.site_visit_debrief.follow_up_strategy}</p>
                )}
              </div>
            )}

            {/* Suggested next task */}
            {structured?.suggested_task && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="suggested-task-block">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Suggested follow-up task</p>
                  <label className="flex items-center gap-1 text-xs text-emerald-700 cursor-pointer">
                    <input type="checkbox" checked={acceptTask} onChange={(e) => setAcceptTask(e.target.checked)} data-testid="accept-task-checkbox" />
                    Create on save
                  </label>
                </div>
                <Input
                  value={structured.suggested_task.title}
                  onChange={(e) => setStructured({ ...structured, suggested_task: { ...structured.suggested_task, title: e.target.value } })}
                  className="text-sm h-8 mb-2"
                  data-testid="suggested-task-title"
                />
                <div className="flex gap-2 text-xs">
                  <Badge variant="outline" className="capitalize">{structured.suggested_task.task_type}</Badge>
                  <Badge variant="outline" className="capitalize">{structured.suggested_task.priority}</Badge>
                  <Badge variant="outline">Due in {structured.suggested_task.due_in_hours}h</Badge>
                </div>
              </div>
            )}

            {/* Suggested stage movement (always opt-in) */}
            {structured?.suggested_stage?.stage && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="suggested-stage-block">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Suggested stage → <span className="capitalize">{structured.suggested_stage.stage.replace(/_/g, " ")}</span>
                  </p>
                  <label className="flex items-center gap-1 text-xs text-blue-700 cursor-pointer">
                    <input type="checkbox" checked={acceptStage} onChange={(e) => setAcceptStage(e.target.checked)} data-testid="accept-stage-checkbox" />
                    Apply on save
                  </label>
                </div>
                {structured.suggested_stage.rationale && (
                  <p className="text-xs text-blue-900 italic">"{structured.suggested_stage.rationale}"</p>
                )}
                {acceptStage && (
                  <select
                    value={structured.suggested_stage.stage}
                    onChange={(e) => setStructured({ ...structured, suggested_stage: { ...structured.suggested_stage, stage: e.target.value } })}
                    className="mt-2 text-sm border border-blue-200 rounded px-2 py-1 bg-white"
                    data-testid="stage-override-select"
                  >
                    {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                )}
              </div>
            )}

            {/* Suggested template (informational only — guardrail: no auto-send) */}
            {structured?.suggested_template?.intent && structured.suggested_template.intent !== "none" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs" data-testid="suggested-template-block">
                <p className="text-amber-700 font-semibold uppercase tracking-wide text-[10px] mb-1">Suggested template</p>
                <p className="text-amber-900 capitalize">
                  {structured.suggested_template.channel} · {structured.suggested_template.intent.replace(/_/g, " ")}
                </p>
                {structured.suggested_template.rationale && (
                  <p className="text-amber-800 italic mt-1">"{structured.suggested_template.rationale}"</p>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="discard-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Discard
              </Button>
              <Button variant="outline" size="sm" disabled={saving} onClick={() => saveToCRM("transcript_only")} data-testid="save-transcript-only-btn">
                Save transcript only
              </Button>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700" disabled={saving} onClick={() => saveToCRM("full")} data-testid="save-to-crm-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Save to CRM
              </Button>
            </div>
          </div>
        )}

        {/* Step: error */}
        {step === "error" && (
          <div className="flex flex-col items-center py-8 space-y-3" data-testid="voice-log-error-step">
            <AlertCircle className="w-10 h-10 text-rose-500" />
            <p className="text-sm font-semibold text-slate-700">Something went wrong</p>
            <p className="text-xs text-slate-500 text-center max-w-md">{errorMsg}</p>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="error-close-btn">
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
              <Button size="sm" onClick={() => setStep("record")} data-testid="error-retry-btn">
                Try again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SignalsBlock({ title, items, color }) {
  if (!items?.length) return null;
  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    blue:    "bg-blue-50 border-blue-200 text-blue-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    slate:   "bg-slate-50 border-slate-200 text-slate-900",
  };
  const labelMap = {
    emerald: "text-emerald-600",
    rose:    "text-rose-600",
    blue:    "text-blue-600",
    amber:   "text-amber-600",
    slate:   "text-slate-600",
  };
  return (
    <div className={`rounded-lg p-2.5 border ${colorMap[color]}`}>
      <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${labelMap[color]}`}>{title}</p>
      <ul className="text-xs space-y-0.5 list-disc list-inside">
        {items.slice(0, 5).map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
